import {supabase} from '../supabase/client';
import {recordHealthEvent} from '../observability/healthService';

export const SUBSCRIPTIONS_TABLE = 'subscriptions_1772930000';

async function getFunctionError(error, fallbackMessage) {
  const response = error?.context;

  if (response && typeof response.json === 'function') {
    try {
      const readableResponse = typeof response.clone === 'function'
        ? response.clone()
        : response;
      const payload = await readableResponse.json();

      if (typeof payload?.error === 'string' && payload.error) {
        return new Error(payload.error);
      }
    } catch {
      // Fall through to the SDK error when the response has no JSON body.
    }
  }

  return new Error(error?.message || fallbackMessage);
}

export async function getSubscription(businessId) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  const {data, error} = await supabase
    .from(SUBSCRIPTIONS_TABLE)
    .select('*')
    .eq('business_id', businessId)
    .maybeSingle();

  if (error) {
    throw await getFunctionError(error, 'Unable to start Stripe checkout.');
  }

  return data;
}

export async function getCurrentSubscription(userId) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  const {data, error} = await supabase
    .from(SUBSCRIPTIONS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .in('status', ['active', 'trialing', 'past_due'])
    .not('stripe_subscription_id', 'is', null)
    .neq('stripe_subscription_id', '')
    .order('updated_at', {ascending: false})
    .order('id', {ascending: false})
    .limit(1)
    .maybeSingle();

  if (error) {
    throw await getFunctionError(error, 'Unable to open Stripe Billing.');
  }

  return data;
}

export async function createCheckoutSession(
  businessId,
  planType,
  billingCycle,
  billingCurrency
) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  if (!businessId || !planType || !billingCycle || !['CAD', 'USD'].includes(billingCurrency)) {
    throw new Error('A Business, plan, billing cycle, and supported billing currency are required.');
  }

  const {data, error} = await supabase.functions.invoke(
    'create-checkout-session',
    {
      body: {
        business_id: businessId,
        plan_type: planType,
        billing_cycle: billingCycle,
        billing_currency: billingCurrency
      }
    }
  );

  if (error) {
    await recordHealthEvent(businessId,'billing','failure','billing_checkout_failed','/settings/billing');
    throw error;
  }

  if (!data?.url) {
    await recordHealthEvent(businessId,'billing','failure','billing_checkout_failed','/settings/billing');
    throw new Error('Stripe did not return a checkout URL.');
  }

  await recordHealthEvent(businessId,'billing','success','billing_checkout_started','/settings/billing');
  return data.url;
}

export async function createPortalSession(businessId, action = 'manage') {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  if (!businessId) {
    throw new Error('Choose a Business before managing billing.');
  }

  const {data, error} = await supabase.functions.invoke(
    'create-portal-session',
    {
      body: {business_id: businessId, action}
    }
  );

  if (error) {
    await recordHealthEvent(businessId,'billing','failure','billing_portal_failed','/settings/billing');
    throw error;
  }

  if (!data?.url) {
    await recordHealthEvent(businessId,'billing','failure','billing_portal_failed','/settings/billing');
    throw new Error('Stripe did not return a portal URL.');
  }

  await recordHealthEvent(businessId,'billing','success','billing_portal_opened','/settings/billing');
  return data.url;
}

export async function changeSubscriptionPlan(businessId, planType, billingCycle) {
  if (!supabase) throw new Error('Supabase is not configured.');
  if (!businessId || !planType || !billingCycle) {
    throw new Error('A Business, plan, and billing cycle are required.');
  }
  const {data, error} = await supabase.functions.invoke('change-subscription-plan', {
    body: {
      business_id: businessId,
      plan_type: planType,
      billing_cycle: billingCycle,
      idempotency_key: crypto.randomUUID()
    }
  });
  if (error) {
    await recordHealthEvent(businessId, 'billing', 'failure', 'billing_plan_change_failed', '/settings/billing');
    throw await getFunctionError(error, 'Unable to change the subscription plan.');
  }
  await recordHealthEvent(businessId, 'billing', 'success', 'billing_plan_change_requested', '/settings/billing');
  return data;
}

export async function getAccountSubscription(userId) {
  const current = await getCurrentSubscription(userId);
  if (current) return current;
  const {data, error} = await supabase.from(SUBSCRIPTIONS_TABLE)
    .select('*').eq('user_id', userId)
    .order('updated_at', {ascending: false}).order('id', {ascending: false})
    .limit(1).maybeSingle();
  if (error) throw error;
  return data;
}
